import { drawerAtom } from '@/store/drawer-atom';
import { useAtomValue } from 'jotai';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';

const loadManagedDrawer = () => import('./managed-drawer');

const ManagedDrawer = dynamic(loadManagedDrawer, { ssr: false });

/** Prefetch the managed-drawer chunk (hover/focus/idle). */
export function preloadManagedDrawer() {
  void loadManagedDrawer();
}

/**
 * Mounts ManagedDrawer only after the first open (or preload), then keeps it
 * mounted so close animations still work.
 */
export default function LazyManagedDrawer() {
  const display = useAtomValue(drawerAtom).display;
  const [shouldMount, setShouldMount] = useState(false);
  const mountedOnce = useRef(false);

  useEffect(() => {
    if (display || mountedOnce.current) {
      mountedOnce.current = true;
      setShouldMount(true);
    }
  }, [display]);

  useEffect(() => {
    if (typeof window === 'undefined' || shouldMount) return;

    let cancelled = false;
    const run = () => {
      if (!cancelled) preloadManagedDrawer();
    };

    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(run, { timeout: 4000 });
    } else {
      timeoutId = setTimeout(run, 2500);
    }

    return () => {
      cancelled = true;
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [shouldMount]);

  if (!shouldMount && !display) return null;
  return <ManagedDrawer />;
}
